'use client'

import {createContext, ReactNode, useContext, useEffect} from 'react'
import {
  SoftwareIndicatorReviewStatus,
  SoftwareEvaluationData,
  SoftwareIndicatorDimension,
  SoftwareQualityAttributeLevelEvaluation,
  SoftwareQualityEvaluator,
  SoftwareEvaluationResult,
  SoftwareIndicatorAttribute,
  SoftwareIndicatorAttributeLevel,
  SoftwareDimensionScore,
  PolygonData,
  SoftwareEvaluationArtefact,
  SoftwareAttributeScore,
  ActivityLogRow,
  SoftwareEvaluationReportingInformation,
  EvaluationData,
  PoFTopicNode} from './apiSoftwareIndicator'
import {useSoftwareEvaluation} from './useSoftwareEvaluation'
import {ArtefactOption} from './SoftwareEvaluationArtefact'
import {EvaluationContactPersonOption} from './SoftwareEvaluationPoFTopic'

type SoftwareEvaluationContextType = {
  softwareId: string,
  evaluationPeriod: string,
  indicatorConfiguration: SoftwareIndicatorDimension[],
  getIndicatorDimensions: () => SoftwareIndicatorDimension[],
  getIndicatorDimensionById: (dimensionId: SoftwareIndicatorDimension['id']) => SoftwareIndicatorDimension | undefined,
  getIndicatorAttributes: (dimensionId?: SoftwareIndicatorDimension['id']) => SoftwareIndicatorAttribute[],
  getIndicatorAttributeById: (dimensionId: SoftwareIndicatorDimension['id'], attributeId: SoftwareIndicatorAttribute['id']) => SoftwareIndicatorAttribute | undefined,
  getIndicatorAttributeLevels: (dimensionId?: SoftwareIndicatorDimension['id'], attributeId?: SoftwareIndicatorAttribute['id']) => SoftwareIndicatorAttributeLevel[],
  updateAttributes: (attributes: {id: SoftwareIndicatorAttribute['id'], name: SoftwareIndicatorAttribute['name'], weight: SoftwareIndicatorAttribute['weight']}[]) => Promise<undefined | string>,
  indicatorConfigurationMaximumLevel: () => number | null,

  evaluationData: EvaluationData | null | undefined
  currentAttributeLevelEvaluations: SoftwareQualityAttributeLevelEvaluation[] | null
  attributeScore: (attributeId: SoftwareIndicatorAttribute['id']) => SoftwareAttributeScore['attribute_score'] | null
  dimensionScore: (dimensionId: SoftwareIndicatorDimension['id']) => SoftwareDimensionScore['dimension_score'] | null
  getLevelEvaluationsByDimensionId: (dimensionId: SoftwareIndicatorDimension['id']) => SoftwareQualityAttributeLevelEvaluation[] | null
  getLevelEvaluationsByAttributeId: (attributeId: SoftwareIndicatorAttribute['id']) => SoftwareQualityAttributeLevelEvaluation[] | null
  getLevelEvaluationByAttributeLevelId: (attributeLevelId: SoftwareIndicatorAttributeLevel['id']) => SoftwareQualityAttributeLevelEvaluation | null
  getDimensionEvaluationProgress: (dimensionId:SoftwareIndicatorDimension['id']) => number
  getAttributeLastUpdatedDate: (attributeId: SoftwareIndicatorAttribute['id']) => Date | null
  getDimensionLastUpdatedDate: (dimensionId: SoftwareIndicatorDimension['id']) => Date | null
  addEvaluation: (attribute_levelId: SoftwareIndicatorAttributeLevel['id'], fulfilled: boolean | null) => void
  minimalPolygon: PolygonData[]
  scorePolygon: PolygonData[]
  attributeStatus: (attributeId: SoftwareIndicatorAttribute['id']) => SoftwareIndicatorReviewStatus | null
  dimensionStatus: (dimensionId: SoftwareIndicatorDimension['id']) => SoftwareIndicatorReviewStatus | null
  evaluationResult: SoftwareEvaluationResult | null
  softwareQualityDimensionStatus: {[k: string]: number}
  attributeEvaluators: (attributeId: SoftwareIndicatorAttribute['id']) => string[]
  dimensionEvaluators: (dimensionId: SoftwareIndicatorDimension['id']) => string[]
  softwareEvaluators: () => SoftwareQualityEvaluator[],
  updateMinimalScores: (scores: {id: SoftwareIndicatorDimension['id'], minimum_required_level: number}[]) => Promise<undefined | string>,
  evaluatedSoftware: {software_id: string, software_slug: string, software_name: string}[]
  softwareEvaluations: SoftwareEvaluationData[],
  currentSoftwareEvaluation: SoftwareEvaluationData | undefined,
  currentArtefact: SoftwareEvaluationArtefact | null,
  saveArtefact: (artefact: string | null) => void,
  currentReportingInformation: SoftwareEvaluationReportingInformation[] | null,
  pofTopics: PoFTopicNode[]
  savePoFTopic: (organisation_id: string, category_id: string) => void,
  saveContactPerson: (organisation_id: string, contactPerson: string | null, account?: string) => void,
  submitSoftwareEvaluation: () => void,
  statusHistory: ActivityLogRow[] | null,
  allQuestionsAnswered: boolean,
  reportingInformationFulfilled: boolean,
  categoriesOfSoftware: Set<string>,
  softwareArtefactOptions: ArtefactOption[]
  softwareContactPersonOptions: EvaluationContactPersonOption[]
  loadAttributeLevelEvaluationHistory: (attribute_id: number) => Promise<SoftwareQualityAttributeLevelEvaluation[] | undefined>
}

const SoftwareEvaluationContext = createContext<SoftwareEvaluationContextType | undefined>(undefined)

type SoftwareEvaluationProviderProps = {
  softwareId: string,
  year: string,
  children: ReactNode
}

export function SoftwareEvaluationProvider({softwareId, year, children}: SoftwareEvaluationProviderProps) {
  const {
    evaluationData,
    currentAttributeLevelEvaluations,
    getDimensionScore: dimensionScore,
    getAttributeScore: attributeScore,
    getDimensionReviewStatus: dimensionStatus,
    getAttributeReviewStatus: attributeStatus,
    addAttributeLevelEvaluation: addEvaluation,
    softwareScorePolygon: scorePolygon,
    evaluationResult,
    softwareQualityDimensionStatus,
    softwareEvaluations,
    submitSoftwareEvaluation,
    currentSoftwareEvaluation,
    evaluatedSoftware,
    currentArtefact,
    saveArtefact,
    currentReportingInformation,
    savePoFTopic,
    saveContactPerson,
    getLevelEvaluationsByDimensionId,
    getLevelEvaluationsByAttributeId,
    getLevelEvaluationByAttributeLevelId,
    getDimensionEvaluationProgress,
    getAttributeLastUpdatedDate,
    getDimensionLastUpdatedDate,
    attributeEvaluators,
    dimensionEvaluators,
    softwareEvaluators,
    statusHistory,
    allQuestionsAnswered,
    reportingInformationFulfilled,
    categoriesOfSoftware,
    softwareArtefactOptions,
    softwareContactPersonOptions,
    loadAttributeLevelEvaluationHistory,
    indicatorConfiguration,
    getIndicatorDimensions,
    getIndicatorDimensionById,
    getIndicatorAttributes,
    getIndicatorAttributeById,
    getIndicatorAttributeLevels,
    minimalPolygon,
    updateMinimalScores,
    updateAttributes,
    indicatorConfigurationMaximumLevel,
    evaluationPeriod,
    pofTopics
  } = useSoftwareEvaluation({software: softwareId, year: year})

  return (
    <SoftwareEvaluationContext.Provider value={
      {
        softwareId,
        evaluationPeriod,
        indicatorConfiguration,
        getIndicatorDimensions,
        getIndicatorDimensionById,
        getIndicatorAttributes,
        getIndicatorAttributeById,
        getIndicatorAttributeLevels,
        indicatorConfigurationMaximumLevel,
        currentAttributeLevelEvaluations,
        evaluationData,
        attributeScore,
        dimensionScore,
        getLevelEvaluationsByDimensionId,
        getLevelEvaluationsByAttributeId,
        getLevelEvaluationByAttributeLevelId,
        getDimensionEvaluationProgress,
        getAttributeLastUpdatedDate,
        getDimensionLastUpdatedDate,
        addEvaluation,
        minimalPolygon,
        scorePolygon,
        attributeStatus,
        dimensionStatus,
        evaluationResult,
        softwareQualityDimensionStatus,
        attributeEvaluators,
        dimensionEvaluators,
        softwareEvaluators,
        softwareEvaluations,
        submitSoftwareEvaluation,
        currentSoftwareEvaluation,
        updateMinimalScores,
        evaluatedSoftware,
        updateAttributes,
        currentArtefact,
        saveArtefact,
        statusHistory,
        currentReportingInformation,
        pofTopics,
        savePoFTopic,
        saveContactPerson,
        allQuestionsAnswered,
        reportingInformationFulfilled,
        categoriesOfSoftware,
        softwareArtefactOptions,
        softwareContactPersonOptions,
        loadAttributeLevelEvaluationHistory
      }
    }>
      {children}
    </SoftwareEvaluationContext.Provider>
  )
}

export function useSoftwareEvaluationContext() {
  const ctx = useContext(SoftwareEvaluationContext)
  if (!ctx) {
    throw new Error('useSoftwareEvaluationContext must be inside SoftwareEvaluationProvider')
  }
  return ctx
}

export function useSoftwareEvaluationData() {
  return useContext(SoftwareEvaluationContext)?.evaluationData
}

export default SoftwareEvaluationContext
